import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Grid,
  CircularProgress,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  Avatar,
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridPaginationModel,
  GridToolbar,
  GridRenderCellParams,
} from '@mui/x-data-grid';
import {
  Search as SearchIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import Moment from 'moment';
import { API_URL } from '../../config';

// Retry helper for transient Lambda cold-start errors (5xx from API Gateway/Lambda)
async function fetchWithRetry(url: string, options: RequestInit, retries = 2, delay = 1500): Promise<Response> {
  for (let i = 0; i <= retries; i++) {
    const response = await fetch(url, options);
    // Retry on 500 (Lambda internal error), 502 (bad gateway), 503 (service unavailable)
    const isServerError = response.status === 500 || response.status === 502 || response.status === 503;
    if (!isServerError || i === retries) return response;
    await new Promise(resolve => setTimeout(resolve, delay));
  }
  throw new Error('fetchWithRetry exhausted'); // unreachable — TypeScript guard
}

interface User {
  id?: string;
  user_id?: string;
  userId?: string;
  email: string;
  name?: string;
  createdAt?: string;
  updatedAt?: string;
  created_at?: string;
  updated_at?: string;
  user_metadata?: {
    firstName?: string;
    lastName?: string;
    username?: string;
    email?: string;
    copyright?: string;
    website?: string;
  };
  app_metadata?: {
    accountType?: string;
    registered?: boolean;
    subscription?: string | number;
    subscriptionId?: string;
    subEndDate?: string | number;
    current?: {
      storage?: number;
      emails?: number;
      photos?: number;
    };
    limits?: {
      storage?: number;
      emails?: number;
      photos?: number;
    };
  };
  templates?: {
    default?: string;
  };
  stripeCustomerId?: string;
}

interface AdminUsersV2Props {
  token: string;
}

const AdminUsersV2: React.FC<AdminUsersV2Props> = ({ token }) => {
  const navigate = useNavigate();
  
  // State for users grid
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<User[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  });
  
  // State for filters and search
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [cursorHistory, setCursorHistory] = useState<string[]>(['']);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [accountTypeFilter, setAccountTypeFilter] = useState('');
  const [subscriptionFilter, setSubscriptionFilter] = useState('');
  
  // State for details modal
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // Fetch users with cursor pagination
  const fetchUsers = useCallback(async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const cursor = cursorHistory[paginationModel.page] || '';
      const params = new URLSearchParams({
        limit: paginationModel.pageSize.toString(),
        ...(cursor && { cursor }),
        ...(debouncedSearchTerm && { search: debouncedSearchTerm }),
      });

      const response = await fetchWithRetry(`${API_URL}/admin/v1/users?${params}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        const usersArray = Array.isArray(data.users) ? data.users : (Array.isArray(data) ? data : []);
        setUsers(usersArray);
        setTotalCount(data.totalCount || 0);
        setHasNextPage(Boolean(data.pagination?.hasNextPage));
        const nextCursor = data.pagination?.nextCursor;
        if (nextCursor !== undefined && nextCursor !== null) {
          setCursorHistory(prev => {
            const next = [...prev];
            next[paginationModel.page + 1] = nextCursor;
            return next;
          });
        }
      } else {
        toast.error(`Failed to fetch users: ${response.status}`);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  // cursorHistory intentionally omitted from deps (same pattern as AdminDashboardV2)
  }, [token, paginationModel, debouncedSearchTerm]);

  // Define columns for DataGrid
  const columns: GridColDef[] = [
    {
      field: 'user_id',
      headerName: 'User ID',
      width: 200,
      renderCell: (params: GridRenderCellParams) => {
        const userId = params.row.user_id || params.row.userId || params.row.id;
        return (
          <Box>
            <Typography variant="body2" fontWeight="medium">
              {params.row.user_metadata?.username || 'N/A'}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {userId}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'email',
      headerName: 'Email',
      width: 250,
    },
    {
      field: 'name',
      headerName: 'Name',
      width: 200,
      renderCell: (params: GridRenderCellParams) => {
        const firstName = params.row.user_metadata?.firstName;
        const lastName = params.row.user_metadata?.lastName;
        const fullName = firstName && lastName ? `${firstName} ${lastName}` : params.value;
        return <Typography variant="body2">{fullName || 'N/A'}</Typography>;
      },
    },
    {
      field: 'accountType',
      headerName: 'Account Type',
      width: 150,
      renderCell: (params: GridRenderCellParams) => {
        const accountType = params.row.app_metadata?.accountType || 'standard';
        return (
          <Chip 
            label={accountType} 
            size="small" 
            color={accountType === 'administrator' ? 'error' : 'default'}
            variant="outlined"
          />
        );
      },
    },
    {
      field: 'subscription',
      headerName: 'Subscription',
      width: 120,
      renderCell: (params: GridRenderCellParams) => {
        const subValue = params.row.app_metadata?.subscription;
        const subMap: Record<string, string> = {
          '0': 'Free',
          '1': 'Starter',
          '2': 'Basic',
          '3': 'Plus',
          '4': 'Premium',
        };
        const subName = subValue !== undefined && subValue !== null
          ? (subMap[String(subValue)] || `Tier ${subValue}`)
          : 'N/A';
        return (
          <Typography variant="body2">{subName}</Typography>
        );
      },
    },
    {
      field: 'registered',
      headerName: 'Registered',
      width: 100,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.row.app_metadata?.registered ? 'Yes' : 'No'}
          size="small"
          color={params.row.app_metadata?.registered ? 'success' : 'warning'}
        />
      ),
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 150,
      renderCell: (params: GridRenderCellParams) => {
        const createdAt = params.row.created_at || params.row.createdAt;
        let displayDate = 'N/A';
        if (createdAt) {
          // Handle Unix timestamp strings (e.g., "1755399009") vs ISO dates
          const numericValue = parseInt(createdAt, 10);
          if (!isNaN(numericValue) && String(numericValue) === String(createdAt)) {
            displayDate = Moment.unix(numericValue).format('YYYY-MM-DD');
          } else {
            displayDate = Moment(createdAt).format('YYYY-MM-DD');
          }
        }
        return (
          <Typography variant="body2">{displayDate}</Typography>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Button
          variant="outlined"
          size="small"
          startIcon={<VisibilityIcon />}
          onClick={() => handleDetailsClick(params.row)}
        >
          Details
        </Button>
      ),
    },
  ];

  // Event handlers
  const handleDetailsClick = (user: User) => {
    setSelectedUser(user);
    setDetailsOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSelectedUser(null);
  };

  const handleRefresh = () => {
    setCursorHistory(['']);
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setCursorHistory(['']);
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  };

  // Filter users based on client-side account/subscription filters (search is server-side)
  const filteredUsers = users.filter(user => {
    const matchesAccountType = !accountTypeFilter || 
      user.app_metadata?.accountType === accountTypeFilter;
    
    const matchesSubscription = !subscriptionFilter || 
      user.app_metadata?.subscription?.toString() === subscriptionFilter;
    
    return matchesAccountType && matchesSubscription;
  });

  // Effects
  // Debounce search input to avoid firing a backend request on every keystroke
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearchTerm(searchTerm), 300);
    return () => clearTimeout(id);
  }, [searchTerm]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          User Management
        </Typography>
        <Typography variant="body1" color="textSecondary">
          View and manage user accounts
        </Typography>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Search users..."
              value={searchTerm}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={3} md={2}>
            <FormControl fullWidth>
              <InputLabel>Account Type</InputLabel>
              <Select
                value={accountTypeFilter}
                label="Account Type"
                onChange={(e) => setAccountTypeFilter(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="standard">Standard</MenuItem>
                <MenuItem value="administrator">Administrator</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3} md={2}>
            <FormControl fullWidth>
              <InputLabel>Subscription</InputLabel>
              <Select
                value={subscriptionFilter}
                label="Subscription"
                onChange={(e) => setSubscriptionFilter(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="0">Free</MenuItem>
                <MenuItem value="1">Starter</MenuItem>
                <MenuItem value="2">Basic</MenuItem>
                <MenuItem value="3">Plus</MenuItem>
                <MenuItem value="4">Premium</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={12} md={4}>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
                disabled={loading}
              >
                Refresh
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Users DataGrid */}
      <Paper sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={filteredUsers}
          columns={columns}
          getRowId={(row) => row.user_id || row.userId || row.id}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[25, 50, 100]}
          rowCount={totalCount}
          paginationMode="server"
          loading={loading}
          slots={{
            toolbar: GridToolbar,
          }}
          slotProps={{
            toolbar: {
              showQuickFilter: false, // We have custom search
            },
          }}
          sx={{
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid #f0f0f0',
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: '#fafafa',
            },
          }}
        />
      </Paper>

      {/* Details Modal */}
      <Dialog
        open={detailsOpen}
        onClose={handleCloseDetails}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              User Details
            </Typography>
            <IconButton onClick={handleCloseDetails}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedUser ? (
            <Box>
              {/* User Avatar and Basic Info */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar sx={{ width: 60, height: 60, mr: 2, bgcolor: 'primary.main' }}>
                      <PersonIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h6">
                        {selectedUser.user_metadata?.firstName} {selectedUser.user_metadata?.lastName}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        @{selectedUser.user_metadata?.username}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {selectedUser.email}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              {/* Detailed Information */}
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">User ID</Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                    {selectedUser.user_id || selectedUser.userId || selectedUser.id}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">Account Type</Typography>
                  <Chip
                    label={selectedUser.app_metadata?.accountType || 'standard'}
                    size="small"
                    color={selectedUser.app_metadata?.accountType === 'administrator' ? 'error' : 'default'}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">Subscription</Typography>
                  <Typography variant="body1">
                    {selectedUser.app_metadata?.subscription?.toString() || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">Registered</Typography>
                  <Typography variant="body1">
                    {selectedUser.app_metadata?.registered ? 'Yes' : 'No'}
                  </Typography>
                </Grid>
                {selectedUser.app_metadata?.subEndDate && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">Subscription End Date</Typography>
                    <Typography variant="body1">
                      {typeof selectedUser.app_metadata.subEndDate === 'number' 
                        ? Moment.unix(selectedUser.app_metadata.subEndDate).format('YYYY-MM-DD HH:mm:ss')
                        : selectedUser.app_metadata.subEndDate !== '0' 
                          ? Moment.unix(parseInt(selectedUser.app_metadata.subEndDate)).format('YYYY-MM-DD HH:mm:ss')
                          : 'No end date'
                      }
                    </Typography>
                  </Grid>
                )}
                {selectedUser.app_metadata?.current?.storage && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">Storage Usage</Typography>
                    <Typography variant="body1">
                      {(selectedUser.app_metadata.current.storage / 1073741824).toFixed(2)} GB
                      {selectedUser.app_metadata.limits?.storage && 
                        ` / ${(selectedUser.app_metadata.limits.storage / 1073741824).toFixed(2)} GB`
                      }
                    </Typography>
                  </Grid>
                )}
                {(selectedUser.created_at || selectedUser.createdAt) && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">Created At</Typography>
                    <Typography variant="body1">
                      {(() => {
                        const dateVal = selectedUser.created_at || selectedUser.createdAt;
                        const numericDate = parseInt(dateVal, 10);
                        if (!isNaN(numericDate) && String(numericDate) === String(dateVal)) {
                          return Moment.unix(numericDate).format('YYYY-MM-DD HH:mm:ss');
                        }
                        return Moment(dateVal).format('YYYY-MM-DD HH:mm:ss');
                      })()}
                    </Typography>
                  </Grid>
                )}
                {(selectedUser.updated_at || selectedUser.updatedAt) && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">Last Updated</Typography>
                    <Typography variant="body1">
                      {(() => {
                        const dateVal = selectedUser.updated_at || selectedUser.updatedAt;
                        const numericDate = parseInt(dateVal, 10);
                        if (!isNaN(numericDate) && String(numericDate) === String(dateVal)) {
                          return Moment.unix(numericDate).format('YYYY-MM-DD HH:mm:ss');
                        }
                        return Moment(dateVal).format('YYYY-MM-DD HH:mm:ss');
                      })()}
                    </Typography>
                  </Grid>
                )}
              </Grid>

              {/* Raw User Data */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Raw User Data
                </Typography>
                <Paper 
                  variant="outlined" 
                  sx={{ 
                    p: 2, 
                    backgroundColor: 'grey.50',
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                    overflow: 'auto',
                    maxHeight: 300
                  }}
                >
                  <pre>{JSON.stringify(selectedUser, null, 2)}</pre>
                </Paper>
              </Box>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetails}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminUsersV2;
